import { IonLoading } from '@ionic/react';

import { DeepLinkListener } from '@/components/listeners';
import {
  useAccount,
  useAuth,
  useClinic,
  useDentist,
  useNetwork,
  useRequests,
  useVersion
} from '@/contexts';
import { useBackButton, useKeyboardAwareInput } from '@/hooks';
import { Authentication, Dashboard, Overlays, Profiles } from '@/layouts';
import { DashboardProviders } from '@/providers';
import { ACCESS_TYPES } from '@/utils/constants';

interface AppLayoutProps {}

const AppLayout: React.FC<AppLayoutProps> = () => {
  const { connected, loading: loadingNetwork } = useNetwork();
  const { forceUpdate, maintenanceModeEnabled } = useVersion();
  const { authenticated, loading: loadingAuth, user } = useAuth();
  const { selectedDentist } = useDentist();
  const { selectedClinic } = useClinic();
  const { pendingRequestsCount } = useRequests();
  /* Back button handler */
  useBackButton();

  /* Keyboard-aware scrolling behavior handler
   * - Automatically scrolls focused input into view when keyboard appears
   * - Prevents keyboard from covering input fields
   * - Improves form accessibility and user experience
   */
  useKeyboardAwareInput();

  /* Display overlays when:
   * - No internet connection
   * - Update is required
   * - Maintenance mode is enabled
   * - Loading network status
   */
  const showOverlays: boolean =
    (!connected || forceUpdate === true || maintenanceModeEnabled === true) &&
    !loadingNetwork;

  /* Overlays */
  if (showOverlays) {
    return <Overlays connected={connected} forceUpdate={forceUpdate} />;
  }

  const getActiveScreen = (): React.ReactNode => {
    /* Authentication */
    if (!authenticated || loadingAuth || loadingNetwork) {
      return <Authentication />;
    }

    /* Profiles */
    if (!selectedDentist || !selectedClinic) {
      return <Profiles />;
    }

    /* Access Control */
    if (
      user &&
      user.id !== selectedClinic.id &&
      selectedDentist.access === ACCESS_TYPES.NONE
    ) {
      return <Overlays noAccess />;
    }

    /* Dashboard */
    return (
      <DashboardProviders>
        <Dashboard pendingRequestsCount={pendingRequestsCount} />
      </DashboardProviders>
    );
  };

  return (
    <>
      {/* Deep Link Listener */}
      <DeepLinkListener />

      {/* Active Screen */}
      {getActiveScreen()}

      {/* Loading */}
      <IonLoading
        isOpen={loadingAuth}
        message="Loading..."
        spinner="crescent"
      />
    </>
  );
};

export default AppLayout;
